// Pure musical-grid math, split out of scene-manager.js so it can be unit
// tested without a browser (same "plain data in, plain data out" reasoning
// orchestrator.js already documents about itself) and so director.js can
// reuse it without reaching into scene-manager's internals.
//
// Two independent pieces:
//   computeBeatGrid   - bpm + a position in seconds -> beat/bar/phrase/
//                       section indices+phases, and a per-beat accent.
//   PositionSmoother  - anti-drift re-anchoring across Sonos polls (~3s
//                       apart), so the extrapolated position scene-manager
//                       feeds into computeBeatGrid every frame doesn't
//                       visibly jump on every poll.

const BEATS_PER_BAR = 4;
const BEATS_PER_PHRASE = 16;
const BEATS_PER_SECTION = 32;

// Temps 1/2/3/4 - the brief's own values: full accent on the downbeat, a
// weaker but still-real accent on 3 (the "second strong beat" in common
// 4/4 phrasing), 2 and 4 the weakest.
const ACCENTS = [1.0, 0.4, 0.62, 0.4];

const NULL_GRID = {
  beatIndex: null, beatPhase: null,
  barIndex: null, barPhase: null,
  phraseIndex: null, phrasePhase: null,
  sectionIndex: null, sectionPhase: null,
  accent: null
};

// bpm/positionSeconds nullable - returns an all-null grid exactly like
// scene-manager.js's own _updateBeatPhase already does for beatPhase/
// beatIndex, so nothing downstream needs a separate "do we have bpm" check.
// beatCount: fractional beats elapsed (not necessarily bpm-derived - see
// track-analysis.js's own lookupBeatPhase, which derives this from
// beat_times_ms directly rather than a constant-tempo extrapolation).
// unitBeats: how many beats make up the unit being split (1 for a beat
// itself, BEATS_PER_BAR for a bar, etc).
export function splitBeatCount(beatCount, unitBeats) {
  const count = beatCount / unitBeats;
  const index = Math.floor(count);
  return { index: index, phase: count - index };
}

export function computeBeatGrid(bpm, positionSeconds) {
  if (!bpm || positionSeconds == null) return NULL_GRID;

  const beatDuration = 60 / bpm;
  const beatCount = Math.max(0, positionSeconds) / beatDuration;

  const beat = splitBeatCount(beatCount, 1);
  const bar = splitBeatCount(beatCount, BEATS_PER_BAR);
  const phrase = splitBeatCount(beatCount, BEATS_PER_PHRASE);
  const section = splitBeatCount(beatCount, BEATS_PER_SECTION);
  const beatInBar = beat.index % BEATS_PER_BAR;

  return {
    beatIndex: beat.index,
    beatPhase: beat.phase,
    barIndex: bar.index,
    barPhase: bar.phase,
    phraseIndex: phrase.index,
    phrasePhase: phrase.phase,
    sectionIndex: section.index,
    sectionPhase: section.phase,
    accent: ACCENTS[beatInBar]
  };
}

export { BEATS_PER_BAR, BEATS_PER_PHRASE, BEATS_PER_SECTION, ACCENTS };

function clamp01(x) {
  return Math.max(0, Math.min(1, x));
}

function smoothstep(edge0, edge1, x) {
  const t = clamp01((x - edge0) / (edge1 - edge0));
  return t * t * (3 - 2 * t);
}

// A correction under this fraction of a beat's smoothing window still
// counts as "essentially on schedule" once blended - kept as a named
// constant purely for CORRECTION_DURATION_S's own documentation below.
const CORRECTION_DURATION_S = 0.4;
// A jump this big or bigger is accepted outright rather than smoothed - a
// half-period at a DEFAULT_HALF_PERIOD_S fallback when bpm is unknown.
const DEFAULT_HALF_PERIOD_S = 0.25;
// Multiplicative confidence penalty applied the instant a big jump is
// accepted, recovering linearly back to 1 over JUMP_RECOVERY_S.
const JUMP_PENALTY = 0.5;
const JUMP_RECOVERY_S = 3;
// Confidence starts decaying once the last poll is older than this...
const STALE_START_S = 5;
// ...and reaches 0 once it's this old. The 5s trigger point is the brief's
// own number; the decay WINDOW past it (how long until 0) isn't specified
// there, so a further 5s ramp is this file's own reasonable choice.
const STALE_ZERO_S = 10;

// Anchor-based extrapolation (unchanged shape from scene-manager.js's old
// _estimatePosition), plus a smoothed re-anchor: instead of snapping to the
// new Sonos-reported position the instant a poll arrives (which visibly
// skips/repeats a beat), small corrections blend in over
// CORRECTION_DURATION_S and only a correction too large to blend
// (>= half a beat period) snaps immediately - accepted, but it costs
// confidence rather than pretending nothing happened.
export class PositionSmoother {
  constructor() {
    this._anchorPosition = null;
    this._anchorClock = 0;
    this._correcting = false;
    this._correctionFromPosition = 0;
    this._correctionStartClock = 0;
    this._jumpPenaltyAppliedAt = null;
  }

  // rawPositionSeconds/bpm/nowSeconds all come straight from the caller
  // (scene-manager.js's setProfile + performance.now()/1000) - kept as
  // plain arguments rather than reaching for a clock itself, so this stays
  // trivially testable with fabricated timestamps.
  reanchor(rawPositionSeconds, bpm, nowSeconds) {
    if (this._anchorPosition == null) {
      this._anchorPosition = rawPositionSeconds;
      this._anchorClock = nowSeconds;
      return;
    }

    const predicted = this._rawEstimate(nowSeconds);
    const error = rawPositionSeconds - predicted;
    const halfPeriod = bpm ? 30 / bpm : DEFAULT_HALF_PERIOD_S;

    if (Math.abs(error) < halfPeriod) {
      this._correctionFromPosition = predicted;
      this._correctionStartClock = nowSeconds;
      this._correcting = true;
    } else {
      this._correcting = false;
      this._jumpPenaltyAppliedAt = nowSeconds;
    }

    this._anchorPosition = rawPositionSeconds;
    this._anchorClock = nowSeconds;
  }

  _rawEstimate(nowSeconds) {
    if (this._anchorPosition == null) return null;
    return Math.max(0, this._anchorPosition + (nowSeconds - this._anchorClock));
  }

  // Smoothly-extrapolated position for the CURRENT frame - what every
  // caller (beat-grid math, lyric-flash timing) should actually read.
  estimate(nowSeconds) {
    if (this._anchorPosition == null) return null;
    const raw = this._rawEstimate(nowSeconds);
    if (!this._correcting) return raw;

    const t = (nowSeconds - this._correctionStartClock) / CORRECTION_DURATION_S;
    if (t >= 1) {
      this._correcting = false;
      return raw;
    }
    // "What the old anchor would still be saying" if the correction never
    // happened, continuing to advance at 1x - blended toward the new
    // anchor's own raw estimate as t goes 0 -> 1.
    const wouldHaveBeen = this._correctionFromPosition + (nowSeconds - this._correctionStartClock);
    return wouldHaveBeen + (raw - wouldHaveBeen) * smoothstep(0, 1, t);
  }

  // 0..1 - see CHANTIER 2's brief: decays on staleness, and takes a hit
  // (recovering over a few seconds) when a jump had to be accepted outright.
  confidence(nowSeconds) {
    if (this._anchorPosition == null) return 0;

    const staleness = nowSeconds - this._anchorClock;
    const staleFactor = staleness <= STALE_START_S
      ? 1
      : 1 - clamp01((staleness - STALE_START_S) / (STALE_ZERO_S - STALE_START_S));

    let jumpFactor = 1;
    if (this._jumpPenaltyAppliedAt != null) {
      const sinceJump = nowSeconds - this._jumpPenaltyAppliedAt;
      jumpFactor = sinceJump >= JUMP_RECOVERY_S
        ? 1
        : lerp(JUMP_PENALTY, 1, clamp01(sinceJump / JUMP_RECOVERY_S));
    }

    return clamp01(staleFactor * jumpFactor);
  }
}

function lerp(a, b, t) {
  return a + (b - a) * t;
}
